/**
 * @file useCanvasPreview.ts
 * @description Живой предпросмотр значения на канве, пока владелец тянет
 * ползунок (phase19.16, партия SB120, этап 1; TD-403; проект
 * docs/phases/phase19.16-materials/uiux-sb120-stage1.md §4, данные
 * db-design-sb120-stage1.md §5).
 *
 *  - `preview(path, value)` шлёт `commora:canvas-preview { blockId, seq,
 *    path, value }` (поля уже зовут `onPreview` не чаще кадра —
 *    `useFramePreview`); путь не из белого списка shared
 *    (`isSectionPreviewPath`) не отправляется;
 *  - канва отвечает `canvas-preview-ack`; ответа нет за 800 мс — предпросмотр
 *    выключается до перезагрузки кабинета (так же ведёт себя канва старой
 *    версии во время выкладки), а `stalled` один раз за сеанс поднимает
 *    плашку «Канва не отвечает…»;
 *  - канва должна быть готова: открыт черновик (на «Сейчас на сайте» и у
 *    снимка слушателя нет) и окно загрузилось (после сохранения окно
 *    пересоздаётся). Пока не готова — не шлём и не ждём ответа; каждая
 *    загрузка черновика снова включает предпросмотр (`canvasPreviewLoaded`
 *    из `BuilderCanvas`), а ожидание ответа прежнего окна не считается;
 *  - запись в форму — как прежде: отпустили ползунок → `onChange` → форма →
 *    `block-draft` через 150 мс; канва снимает наложенное значение сама.
 */

import { isSectionPreviewPath } from "@chaiberi/shared";
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

import { canvasPreviewAckOf, canvasPreviewMessage, postToCanvas } from "../../lib/site-view/canvas-messages";

/** Сколько ждать ответа канвы. */
export const CANVAS_PREVIEW_ACK_MS = 800;

/** Текст плашки (uiux §4). */
export const CANVAS_PREVIEW_STALLED_TEXT = "Канва не отвечает — значение применится, когда отпустите ползунок.";

/** Состояние сеанса: выключен ли предпросмотр и показана ли уже плашка. */
const session = { disabled: false, noticed: false };
/** Канва: готова ли принимать предпросмотр и номер загрузки окна. */
const canvas = { ready: false, generation: 0 };

/**
 * Окно канвы загрузилось (`onLoad` iframe). `editable` — черновик своей
 * страницы (не «Сейчас на сайте», не снимок): только тогда есть слушатель.
 * Новая загрузка снова включает предпросмотр, выключенный молчанием канвы.
 */
export function canvasPreviewLoaded(editable: boolean): void {
  canvas.generation += 1;
  canvas.ready = editable;
  session.disabled = false;
}

/** Окно канвы пересоздаётся (новый адрес, сохранение, смена режима) — пока не готово. */
export function canvasPreviewUnloaded(): void {
  canvas.generation += 1;
  canvas.ready = false;
}

/** Сброс состояния сеанса — только для тестов. */
export function resetCanvasPreviewSession(): void {
  session.disabled = false;
  session.noticed = false;
  canvas.ready = false;
  canvas.generation = 0;
}

export interface CanvasPreview {
  preview: (path: string, value: unknown) => void;
  /** Канва не ответила — показать плашку (один раз за сеанс). */
  stalled: boolean;
}

export function useCanvasPreview(blockId: string, iframeRef: RefObject<HTMLIFrameElement | null>): CanvasPreview {
  const [stalled, setStalled] = useState(false);
  const seq = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Ждать ответа; не пришёл — выключить предпросмотр до перезагрузки. */
  const arm = useCallback(() => {
    if (timer.current !== null) return;
    const generation = canvas.generation;
    timer.current = setTimeout(() => {
      timer.current = null;
      // Окно за это время перезагрузили или убрали — молчание не его вина.
      if (generation !== canvas.generation || !canvas.ready) return;
      session.disabled = true;
      if (!session.noticed) {
        session.noticed = true;
        setStalled(true);
      }
    }, CANVAS_PREVIEW_ACK_MS);
  }, []);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const frame = iframeRef.current;
      if (!frame || event.source !== frame.contentWindow) return;
      const ack = canvasPreviewAckOf(event.data);
      if (!ack || ack.blockId !== blockId) return;
      // Любой ответ — канва жива; есть неотвеченные позже — ждём их заново.
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
      if (ack.seq < seq.current) arm();
    }
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [arm, blockId, iframeRef]);

  const preview = useCallback(
    (path: string, value: unknown) => {
      if (session.disabled || !canvas.ready || !isSectionPreviewPath(path)) return;
      seq.current += 1;
      postToCanvas(iframeRef.current, canvasPreviewMessage(blockId, seq.current, path, value));
      // Ждём ответ на первое из неотвеченных: пока ждём, таймер не перезапускаем.
      arm();
    },
    [arm, blockId, iframeRef],
  );

  return { preview, stalled };
}
